import {
    TextField,
    Button,
    useTheme,
    useMediaQuery,
    Autocomplete,
    CircularProgress,
    Box,
} from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { GenericFormComponentProps } from '../types';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';

import styles from './FormAddress.module.scss';
import { LocationOn } from '@mui/icons-material';
type FormTextProps = GenericFormComponentProps;

/**
 * Form input for autocomplete address using google's Geolocation and Places API
 * @see
 * places api - https://developers.google.com/maps/documentation/javascript/places
 * geolocation api - https://developers.google.com/maps/documentation/javascript/geolocation
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @returns FormAddress component
 */
export function FormAddress({
    field,
    label,
    disabled,
    className,
}: FormTextProps) {
    const { control, getFieldState, setValue } = useFormContext();
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    const [findingMyLocation, setFindingMyLocation] = useState(false);

    // Button callback to fetch user's location and set it as the input value
    const handleFindMyLocation = () => {
        if (!window.google) {
            return;
        }

        setFindingMyLocation(true);
        const geocoder = new window.google.maps.Geocoder();

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                geocoder
                    .geocode({ location: { lat, lng } })
                    .then((resp) => {
                        const closestAddress =
                            resp.results[0].formatted_address;
                        setValue(field, closestAddress);
                        setAddressInputValue(closestAddress);
                    })
                    .finally(() => setFindingMyLocation(false));
            },
            () =>
                alert(
                    'You must enable location services in your browser to use this feature'
                )
        );
    };

    // Store address suggestions from google api
    const [addressSuggestions, setAddressSuggestions] = useState<Array<string>>(
        []
    );

    // Function to check if a string input is a lat/lng pair
    function getLatLngFromInput(input: string) {
        const regex = /^([-+]?\d{1,2}(.\d+)?),\s*([-+]?\d{1,3}(.\d+)?)$/;
        const isLatLng = regex.test(input);
        if (isLatLng) {
            const latLng = input.replaceAll(' ', '').split(',');
            return {
                lat: parseFloat(latLng[0]),
                lng: parseFloat(latLng[1]),
            };
        } else {
            return null;
        }
    }

    // Pull the address from the form context and set it as the initial input value
    const { watch } = useFormContext();
    const address = watch(field);
    const [addressInputValue, setAddressInputValue] = useState(address);

    // Watch address input value for changes, generate suggestions based on input
    useEffect(() => {
        if (!window.google) {
            return;
        }
        if (addressInputValue) {
            const latLngArr = getLatLngFromInput(addressInputValue);

            /*
                Fetch address from lat/lng input
            */
            if (latLngArr) {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode(
                    { location: latLngArr },
                    (response, status) => {
                        if (status === 'OK') {
                            const filteredResponse = response?.filter(
                                (result) => {
                                    return !result.types.includes('plus_code');
                                }
                            );
                            if (filteredResponse) {
                                const addresses = [
                                    `${latLngArr.lat}, ${latLngArr.lng}`,
                                    filteredResponse[0].formatted_address,
                                ];
                                if (addresses) {
                                    setAddressSuggestions(addresses);
                                } else {
                                    setAddressSuggestions([]);
                                }
                            }
                        }
                    }
                );
            } else {
                /*
                Fetch address from text input
            */
                const places =
                    new window.google.maps.places.AutocompleteService();
                places.getPlacePredictions(
                    { input: addressInputValue },
                    (response, status) => {
                        if (status === 'OK') {
                            const addresses =
                                response &&
                                response.map((result) => {
                                    return result.description;
                                });
                            if (addresses) {
                                setAddressSuggestions(addresses);
                            } else {
                                setAddressSuggestions([]);
                            }
                        }
                    }
                );
            }
        } else {
            setAddressSuggestions([]);
        }
    }, [addressInputValue]);

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                label = label || convertToDisplayName(field);
                const id = `address-field-${field}`;
                const helperText = !error
                    ? undefined
                    : error.message ??
                      'Required to at least put closest known address (town/city)';

                // Handle selection change
                const handleSelectionChange = (
                    event: React.SyntheticEvent,
                    value: string | null
                ) => {
                    if (!value) {
                        value = '';
                    }
                    fieldProps.onChange(value);
                    setAddressInputValue(value);
                };

                const handleAddressInputChange = (
                    e: React.ChangeEvent<HTMLInputElement>
                ) => {
                    let value = e.target.value;
                    if (!value) {
                        value = '';
                    }
                    fieldProps.onChange(value);
                    setAddressInputValue(value);
                };

                return (
                    <div
                        className={`${styles.addressContainer} ${
                            isTabletOrMobile
                                ? styles.addressContainerTablet
                                : ''
                        } `}
                    >
                        <Autocomplete
                            id={id}
                            className={className}
                            fullWidth
                            options={addressSuggestions}
                            disabled={disabled}
                            filterOptions={(x) => x}
                            onChange={handleSelectionChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        value: addressInputValue,
                                    }}
                                    error={!!error}
                                    helperText={helperText}
                                    label={label}
                                    variant="standard"
                                    onChange={handleAddressInputChange}
                                    color={error?.message ? 'error' : 'primary'}
                                />
                            )}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleFindMyLocation}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    borderRadius: 50,
                                    marginLeft: '5px',
                                }}
                            >
                                {findingMyLocation ? (
                                    <CircularProgress
                                        size={20}
                                        color="inherit"
                                    />
                                ) : (
                                    <LocationOn fontSize="small" />
                                )}
                            </Button>
                        </Box>
                    </div>
                );
            }}
        />
    );
}
